import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

function Dashboard() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Get location state for success messages
  const location = useLocation();
  
  useEffect(() => {
    // Check for success message in location state
    if (location.state?.success && location.state?.message) {
      setSuccessMessage(location.state.message);
      setShowSuccess(true);
    }
  }, [location]);
  
  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Import the API service
        const api = (await import('../services/api')).default;
        
        // Fetch quotes if user is logged in
        try {
          if (currentUser && currentUser.uid) {
            const quotesResponse = await api.getQuotes(currentUser.uid);
            console.log('Quotes response:', quotesResponse.data);
            
            if (quotesResponse.data && Array.isArray(quotesResponse.data)) {
              // Format quotes for display
              const formattedQuotes = quotesResponse.data.map(quote => ({
                id: quote.id,
                serviceType: quote.job_type || 'Unknown Service',
                date: new Date(quote.created_at || Date.now()).toLocaleDateString(),
                amount: `$${quote.amount?.toFixed(2) || '0.00'}`,
                status: quote.status || 'pending'
              }));
              
              setRecentQuotes(formattedQuotes);
            } else {
              // Fallback to empty array if no quotes or invalid response
              setRecentQuotes([]);
            }
          } else {
            // User not logged in or no UID
            console.log('User not logged in or missing UID');
            setRecentQuotes([]);
          }
        } catch (quoteErr) {
          console.error('Error fetching quotes:', quoteErr);
          // Set empty array for quotes when API fails
          setRecentQuotes([]);
          setError('Unable to fetch quotes. Please try again later.');
        }
        
        // Try to fetch appointments if user is logged in
        try {
          if (currentUser && currentUser.uid) {
            try {
              const appointmentsResponse = await api.getAppointments(currentUser.uid);
              
              if (appointmentsResponse.data && Array.isArray(appointmentsResponse.data)) {
                // Format appointments for display
                const formattedAppointments = appointmentsResponse.data.map(apt => ({
                  id: apt.id,
                  serviceType: apt.service_type || 'Unknown Service',
                  date: new Date(apt.start_time || Date.now()).toLocaleDateString(),
                  time: new Date(apt.start_time || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  provider: apt.provider_name || 'Assigned Provider'
                }));
                
                setUpcomingAppointments(formattedAppointments);
              } else {
                // Fallback to empty array if no appointments or invalid response
                console.log('No appointments found or invalid response format');
                setUpcomingAppointments([]);
              }
            } catch (apiErr) {
              // Handle API errors gracefully
              console.error('API error fetching appointments:', apiErr);
              console.log('Using empty appointments array due to API error');
              setUpcomingAppointments([]);
            }
          } else {
            // User not logged in or no UID
            console.log('User not logged in or missing UID');
            setUpcomingAppointments([]);
          }
        } catch (aptErr) {
          console.error('Error in appointments fetch logic:', aptErr);
          setUpcomingAppointments([]);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Success message */}
      <Snackbar open={showSuccess} autoHideDuration={6000} onClose={handleCloseSuccess}>
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      
      <Typography variant="h6" gutterBottom>
        Welcome back, {currentUser?.email || 'User'}
      </Typography>
      
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button 
                  component={RouterLink} 
                  to="/request-quote" 
                  variant="contained" 
                  fullWidth
                >
                  Request New Quote
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button 
                  component={RouterLink} 
                  to="/quotes" 
                  variant="outlined" 
                  fullWidth
                >
                  View Quote History
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button 
                  component={RouterLink} 
                  to="/profile" 
                  variant="outlined" 
                  fullWidth
                >
                  Update Profile
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Upcoming Appointments */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Upcoming Appointments
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {upcomingAppointments.length > 0 ? (
              <List>
                {upcomingAppointments.map((appointment) => (
                  <ListItem 
                    key={appointment.id}
                    sx={{ 
                      mb: 1, 
                      bgcolor: 'background.paper', 
                      borderRadius: 1,
                      boxShadow: 1
                    }}
                  >
                    <ListItemText
                      primary={appointment.serviceType}
                      secondary={
                        <React.Fragment>
                          <Typography variant="body2" component="span">
                            {appointment.date} at {appointment.time}
                          </Typography>
                          <br />
                          <Typography variant="body2" component="span">
                            Provider: {appointment.provider}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No upcoming appointments.
              </Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Recent Quotes */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Recent Quotes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {recentQuotes.length > 0 ? (
              <List>
                {recentQuotes.map((quote) => (
                  <ListItem 
                    key={quote.id}
                    sx={{ 
                      mb: 1, 
                      bgcolor: 'background.paper', 
                      borderRadius: 1,
                      boxShadow: 1
                    }}
                    button
                    component={RouterLink}
                    to={`/quotes/${quote.id}`}
                  >
                    <ListItemText
                      primary={quote.serviceType}
                      secondary={
                        <React.Fragment>
                          <Typography variant="body2" component="span">
                            {quote.date} - {quote.amount}
                          </Typography>
                          <br />
                          <Typography 
                            variant="body2" 
                            component="span"
                            sx={{
                              color: quote.status === 'Accepted' ? 'success.main' : 
                                    quote.status === 'Rejected' ? 'error.main' : 'info.main'
                            }}
                          >
                            Status: {quote.status}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No recent quotes.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
